import { HttpClient } from '@angular/common/http';
import { Injectable, inject, Injector, makeStateKey, TransferState, PLATFORM_ID, REQUEST_CONTEXT } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateLoader, TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, retry, tap } from 'rxjs/operators';
import { IServerRequestContext } from '../../models';

/**
 * Universal TranslateLoader that works for both SSR and CSR
 * Combines functionality from cloudfront (SSR + TransferState) and designer (CSR + retry)
 */
@Injectable({
  providedIn: 'root'
})
export class UniversalTranslateLoader implements TranslateLoader {
  private transferState = inject(TransferState);
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private injector = inject(Injector);
  /** Set by server.ts while rendering on the server, null in the browser */
  private requestContext = inject(REQUEST_CONTEXT, { optional: true }) as IServerRequestContext | null;
  
  constructor(
    private prefix: string = './i18n/',
    private suffix: string = '.json'
  ) {}

  /**
   * Gets the translations from JSON files
   * Uses TransferState for SSR optimization when available
   * @param lang Language code (e.g., 'de-DE', 'en-US')
   * @returns Observable with translation object
   */
  public getTranslation(lang: string): Observable<any> {
    const key = makeStateKey<any>(`translate-${lang}`);
    const isBrowser = isPlatformBrowser(this.platformId);
    
    // Check if translations exist in TransferState (from SSR)
    if (isBrowser && this.transferState.hasKey(key)) {
      const data = this.transferState.get(key, null);
      if (data) {
        // Use TransferState data and remove it to avoid memory leaks
        this.transferState.remove(key);
        if (typeof process === 'undefined' || process.env['NODE_ENV'] !== 'test') {
          console.log(`[UniversalTranslateLoader] Loading ${lang} from TransferState`);
        }
        return of(data);
      }
    }

    // Load translations from HTTP
    if (typeof process === 'undefined' || process.env['NODE_ENV'] !== 'test') {
      console.log(`[UniversalTranslateLoader] Loading ${lang} from HTTP`);
    }
    // On the server the relative path would resolve against the public host of the request; the files lie on this server.
    const path = `${this.prefix}${lang}${this.suffix}`;
    const localOrigin = this.requestContext?.localOrigin;
    // Not in the HTTP transfer cache: the translations go into the TransferState under their own key above, and the
    // browser would never hit the cached entry anyway (the server loads them from its local origin), so it only
    // doubled the translations in the page HTML.
    return this.http.get(localOrigin ? new URL(path, `${localOrigin}/`).href : path, { transferCache: false }).pipe(
      retry(1), // Retry once on failure
      tap(translations => {
        // Store in TransferState only during SSR for hydration
        if (!isBrowser) {
          // Only the languages still in use when the page is serialized: a language the server switched away from
          // (e.g. the browser language before the page response named the site language) is never read by the browser.
          // The TranslateService is resolved here and not injected, as it depends on this loader.
          this.transferState.onSerialize(key, () => {
            const translate = this.injector.get(TranslateService);
            return [translate.getCurrentLang(), translate.getFallbackLang()].includes(lang) ? translations : undefined;
          });
          if (typeof process === 'undefined' || process.env['NODE_ENV'] !== 'test') {
            console.log(`[UniversalTranslateLoader] Stored ${lang} in TransferState for hydration`);
          }
        }
      }),
      catchError(error => {
        if (typeof process === 'undefined' || process.env['NODE_ENV'] !== 'test') {
          console.error(`[UniversalTranslateLoader] Failed to load translations for ${lang}:`, error);
          console.warn(`[UniversalTranslateLoader] Falling back to empty translations for ${lang}`);
        }
        // Return empty object to prevent app from breaking
        return of({});
      })
    );
  }
}

/**
 * Factory function for creating the Universal TranslateLoader
 * Required for AoT compilation and dependency injection
 */
export function createUniversalTranslateLoader(_http: HttpClient) {
  return new UniversalTranslateLoader('./i18n/', '.json');
}

/**
 * Factory function without parameters - for simpler usage
 */
export function universalTranslateLoaderFactory() {
  return new UniversalTranslateLoader();
}